import { readCookiePreferences } from './cookiePreferences';

const ATTRIBUTION_FRAGMENT_KEY = 'lovibook_attribution';
const ATTRIBUTION_SESSION_KEY = 'lovibook-acquisition-attribution';
const MAX_VALUE_LENGTH = 500;
const MAX_LANDING_PATH_LENGTH = 200;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type AcquisitionAttribution = {
  attributionId: string;
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  campaignId?: string;
  adGroupId?: string;
  adId?: string;
  creativeId?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  landingPath: string;
  capturedAt: string;
};

let activeAttribution: AcquisitionAttribution | null = null;

function cleanValue(value: string | null): string | undefined {
  const cleaned = value?.trim();
  return cleaned ? cleaned.slice(0, MAX_VALUE_LENGTH) : undefined;
}

function cleanUnknownValue(value: unknown) {
  return typeof value === 'string' ? cleanValue(value) : undefined;
}

function normalizeAttribution(value: unknown): AcquisitionAttribution | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  const attributionId = cleanUnknownValue(candidate.attributionId);
  const capturedAt = cleanUnknownValue(candidate.capturedAt);
  const landingPath = cleanUnknownValue(candidate.landingPath)?.slice(0, MAX_LANDING_PATH_LENGTH);
  if (!attributionId || !UUID_PATTERN.test(attributionId)
      || !capturedAt || Number.isNaN(Date.parse(capturedAt))
      || !landingPath?.startsWith('/') || landingPath.includes('?') || landingPath.includes('#')) {
    return null;
  }
  const attribution: AcquisitionAttribution = {
    attributionId,
    source: cleanUnknownValue(candidate.source),
    medium: cleanUnknownValue(candidate.medium),
    campaign: cleanUnknownValue(candidate.campaign),
    term: cleanUnknownValue(candidate.term),
    content: cleanUnknownValue(candidate.content),
    campaignId: cleanUnknownValue(candidate.campaignId),
    adGroupId: cleanUnknownValue(candidate.adGroupId),
    adId: cleanUnknownValue(candidate.adId),
    creativeId: cleanUnknownValue(candidate.creativeId),
    gclid: cleanUnknownValue(candidate.gclid),
    gbraid: cleanUnknownValue(candidate.gbraid),
    wbraid: cleanUnknownValue(candidate.wbraid),
    fbclid: cleanUnknownValue(candidate.fbclid),
    landingPath,
    capturedAt: new Date(capturedAt).toISOString(),
  };
  return attribution.source || attribution.medium || attribution.campaign
    || attribution.campaignId || attribution.adId || attribution.gclid
    || attribution.gbraid || attribution.wbraid || attribution.fbclid
    ? attribution
    : null;
}

function readFirst(params: URLSearchParams, ...names: string[]) {
  for (const name of names) {
    const value = cleanValue(params.get(name));
    if (value) {
      return value;
    }
  }
  return undefined;
}

function canPersistAttribution() {
  const preferences = readCookiePreferences();
  return preferences.analytics || preferences.marketing;
}

function persistAttribution(attribution: AcquisitionAttribution) {
  try {
    if (canPersistAttribution()) {
      window.sessionStorage.setItem(ATTRIBUTION_SESSION_KEY, JSON.stringify(attribution));
    } else {
      window.sessionStorage.removeItem(ATTRIBUTION_SESSION_KEY);
    }
  } catch {
    // Attribution persistence is optional and must never block account flows.
  }
}

function restorePersistedAttribution(): AcquisitionAttribution | null {
  if (!canPersistAttribution()) {
    return null;
  }
  let stored: string | null;
  try {
    stored = window.sessionStorage.getItem(ATTRIBUTION_SESSION_KEY);
  } catch {
    return null;
  }
  if (!stored) {
    return null;
  }
  try {
    return normalizeAttribution(JSON.parse(stored));
  } catch {
    try {
      window.sessionStorage.removeItem(ATTRIBUTION_SESSION_KEY);
    } catch {
      // Ignore unavailable optional storage.
    }
    return null;
  }
}

function encodeAttribution(attribution: AcquisitionAttribution) {
  return window.btoa(encodeURIComponent(JSON.stringify(attribution)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function decodeAttribution(value: string): AcquisitionAttribution | null {
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
    return normalizeAttribution(JSON.parse(decodeURIComponent(window.atob(`${normalized}${padding}`))));
  } catch {
    return null;
  }
}

function attributionFromSearch(location: Location): AcquisitionAttribution | null {
  const params = new URLSearchParams(location.search);
  const source = readFirst(params, 'utm_source');
  const medium = readFirst(params, 'utm_medium');
  const campaign = readFirst(params, 'utm_campaign');
  const term = readFirst(params, 'utm_term');
  const content = readFirst(params, 'utm_content');
  const campaignId = readFirst(params, 'campaign_id', 'campaignid', 'utm_id');
  const adGroupId = readFirst(params, 'ad_group_id', 'adgroupid', 'adset_id');
  const adId = readFirst(params, 'ad_id', 'adid');
  const creativeId = readFirst(params, 'creative_id', 'creative') ?? content;
  const gclid = readFirst(params, 'gclid');
  const gbraid = readFirst(params, 'gbraid');
  const wbraid = readFirst(params, 'wbraid');
  const fbclid = readFirst(params, 'fbclid');

  if (!source && !medium && !campaign && !campaignId && !adId
      && !gclid && !gbraid && !wbraid && !fbclid) {
    return null;
  }

  return {
    attributionId: window.crypto.randomUUID(),
    source,
    medium,
    campaign,
    term,
    content,
    campaignId,
    adGroupId,
    adId,
    creativeId,
    gclid,
    gbraid,
    wbraid,
    fbclid,
    landingPath: location.pathname,
    capturedAt: new Date().toISOString(),
  };
}

function attributionFromFragment(location: Location): AcquisitionAttribution | null {
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  const encoded = params.get(ATTRIBUTION_FRAGMENT_KEY);
  if (!encoded) {
    return null;
  }
  const attribution = decodeAttribution(encoded);
  window.history.replaceState(null, '', `${location.pathname}${location.search}`);
  return attribution;
}

export function captureAcquisitionAttribution(location: Location = window.location) {
  const captured = attributionFromFragment(location)
    ?? activeAttribution
    ?? attributionFromSearch(location)
    ?? restorePersistedAttribution();
  if (captured) {
    activeAttribution = captured;
    persistAttribution(captured);
  }
  return captured;
}

export function getAcquisitionAttribution() {
  return activeAttribution ?? captureAcquisitionAttribution();
}

export function buildSignupAttributionFragment() {
  const attribution = getAcquisitionAttribution();
  return attribution
    ? `#${ATTRIBUTION_FRAGMENT_KEY}=${encodeURIComponent(encodeAttribution(attribution))}`
    : '';
}

export function syncAcquisitionAttributionConsent() {
  if (!canPersistAttribution()) {
    try {
      window.sessionStorage.removeItem(ATTRIBUTION_SESSION_KEY);
    } catch {
      // Ignore unavailable optional storage.
    }
  } else if (activeAttribution) {
    persistAttribution(activeAttribution);
  }
}
